import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { toast } from 'react-toastify';
import {
  PayoutRequestDetail as PayoutRequestDetailType,
  transactionsService,
} from '../../services/transactions.service';

const formatMoney = (value: number) =>
  `QAR ${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (epochMs?: number | string) => {
  if (!epochMs) return '—';
  const date = typeof epochMs === 'number' ? new Date(epochMs) : new Date(epochMs);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const PayoutRequestDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<PayoutRequestDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [paidDialogOpen, setPaidDialogOpen] = useState(false);
  const [failDialogOpen, setFailDialogOpen] = useState(false);
  const [bankReference, setBankReference] = useState('');
  const [failureReason, setFailureReason] = useState('');

  const fetchDetail = async () => {
    if (!id) {
      setError('Payout request ID is missing');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await transactionsService.getPayoutRequestDetails(id);
      if (!response?.success || !response.data) {
        setError('Payout request not found');
        setDetail(null);
        return;
      }
      setDetail(response.data);
    } catch (err) {
      console.error('Failed to fetch payout request details:', err);
      setError(err instanceof Error ? err.message : 'Failed to load payout request');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [id]);

  const handleConfirmPaid = async () => {
    if (!detail || !bankReference.trim()) {
      toast.error('Please provide a bank / transaction reference');
      return;
    }
    try {
      await transactionsService.markPayoutRequestPaid(detail.payoutRequestId, bankReference.trim());
      toast.success('Payout request marked as paid');
      setPaidDialogOpen(false);
      setBankReference('');
      fetchDetail();
    } catch (err) {
      console.error(err);
      toast.error('Failed to mark payout as paid');
    }
  };

  const handleConfirmFailed = async () => {
    if (!detail || !failureReason.trim()) {
      toast.error('Please provide a failure reason');
      return;
    }
    try {
      await transactionsService.markPayoutRequestFailed(detail.payoutRequestId, failureReason.trim());
      toast.success('Payout request marked as failed');
      setFailDialogOpen(false);
      setFailureReason('');
      fetchDetail();
    } catch (err) {
      console.error(err);
      toast.error('Failed to mark payout as failed');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !detail) {
    return (
      <Box sx={{ padding: { xs: '24px 16px', sm: '40px 40px', md: '40px 80px' } }}>
        <Typography color="error" sx={{ fontSize: 'var(--palette-light-font-size-h3-value)' }}>
          {error || 'Payout request not found'}
        </Typography>
        <Button onClick={() => navigate('/transactions')} sx={{ marginTop: '16px' }}>
          Back to Transactions
        </Button>
      </Box>
    );
  }

  const statusLabel = detail.status.charAt(0).toUpperCase() + detail.status.slice(1);

  return (
    <Box
      className="page-container"
      sx={{
        padding: { xs: '24px 16px', sm: '40px 40px', md: '40px 80px' },
        gap: '24px',
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <IconButton onClick={() => navigate('/transactions')} sx={{ borderRadius: '16px' }}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography
            variant="h1"
            sx={{
              fontSize: '32px',
              fontWeight: 700,
              color: 'var(--palette-light-text-primary-value-hex)',
            }}
          >
            Payout request
          </Typography>
        </Box>

        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ borderRadius: '16px' }}>
          <MoreHorizIcon sx={{ color: 'var(--palette-light-brand-primary-value-hex)' }} />
        </IconButton>
      </Box>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {detail.status !== 'paid' && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setPaidDialogOpen(true);
            }}
          >
            Mark as Paid
          </MenuItem>
        )}
        {detail.status === 'new' && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setFailDialogOpen(true);
            }}
          >
            Mark as Failed
          </MenuItem>
        )}
      </Menu>

      <Box
        sx={{
          backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
          borderRadius: '16px',
          padding: '24px',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: '20px',
        }}
      >
        <Box>
          <Typography sx={{ color: 'var(--palette-light-text-secondary-value-hex)', mb: 0.5 }}>
            Vendor
          </Typography>
          <Typography sx={{ fontWeight: 700, color: 'var(--palette-light-brand-primary-value-hex)' }}>
            {detail.vendorName}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ color: 'var(--palette-light-text-secondary-value-hex)', mb: 0.5 }}>
            Status
          </Typography>
          <Typography sx={{ fontWeight: 700 }}>{statusLabel}</Typography>
        </Box>
        <Box>
          <Typography sx={{ color: 'var(--palette-light-text-secondary-value-hex)', mb: 0.5 }}>
            Net payout
          </Typography>
          <Typography sx={{ fontWeight: 700 }}>{formatMoney(detail.amount)}</Typography>
        </Box>
        <Box>
          <Typography sx={{ color: 'var(--palette-light-text-secondary-value-hex)', mb: 0.5 }}>
            Eventi fees
          </Typography>
          <Typography sx={{ fontWeight: 700 }}>{formatMoney(detail.feeAmount)}</Typography>
        </Box>
        <Box>
          <Typography sx={{ color: 'var(--palette-light-text-secondary-value-hex)', mb: 0.5 }}>
            Gross (orders)
          </Typography>
          <Typography sx={{ fontWeight: 700 }}>{formatMoney(detail.grossAmount)}</Typography>
        </Box>
        <Box>
          <Typography sx={{ color: 'var(--palette-light-text-secondary-value-hex)', mb: 0.5 }}>
            Period
          </Typography>
          <Typography sx={{ fontWeight: 700 }}>
            {formatDate(detail.periodStart)} – {formatDate(detail.periodEnd)}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ color: 'var(--palette-light-text-secondary-value-hex)', mb: 0.5 }}>
            Bank account
          </Typography>
          <Typography sx={{ fontWeight: 700 }}>
            {detail.bankAccountName || '—'}
            {detail.bankName ? ` · ${detail.bankName}` : ''}
          </Typography>
          <Typography sx={{ color: 'var(--palette-light-text-secondary-value-hex)' }}>
            {detail.bankAccountNumber || 'No account number on file'}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ color: 'var(--palette-light-text-secondary-value-hex)', mb: 0.5 }}>
            Bank reference
          </Typography>
          <Typography sx={{ fontWeight: 700 }}>{detail.bankReference || '—'}</Typography>
          {detail.paidAt && (
            <Typography sx={{ color: 'var(--palette-light-text-secondary-value-hex)' }}>
              Paid at {formatDate(detail.paidAt)}
            </Typography>
          )}
          {detail.failureReason && (
            <Typography sx={{ color: 'var(--palette-light-status-danger-value-hex)' }}>
              {detail.failureReason}
            </Typography>
          )}
        </Box>
      </Box>

      <Box>
        <Typography
          sx={{
            fontSize: '24px',
            fontWeight: 700,
            mb: '16px',
            color: 'var(--palette-light-text-primary-value-hex)',
          }}
        >
          Included orders ({detail.orderCount})
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: '16px',
            padding: '12px 0',
            borderBottom: '1px solid var(--palette-light-border-primary-value-hex)',
          }}
        >
          {['Order', 'Completed', 'Gross', 'Fee', 'Vendor payout'].map((label) => (
            <Box key={label} sx={{ flex: label === 'Order' ? 1.5 : 1 }}>
              <Typography
                sx={{
                  fontSize: 'var(--palette-light-font-size-small-value)',
                  color: 'var(--palette-light-text-secondary-value-hex)',
                }}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>

        {detail.orders.map((order) => (
          <Box
            key={order.orderId}
            onClick={() => navigate(`/orders/${order.orderId}`)}
            sx={{
              display: 'flex',
              gap: '16px',
              padding: '12px 0',
              borderBottom: '1px solid var(--palette-light-border-primary-value-hex)',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
              },
            }}
          >
            <Box sx={{ flex: 1.5 }}>
              <Typography sx={{ color: 'var(--palette-light-brand-primary-value-hex)' }}>
                {order.orderId.slice(0, 8)}…
              </Typography>
              <Typography
                sx={{
                  fontSize: 'var(--palette-light-font-size-small-value)',
                  color: 'var(--palette-light-text-secondary-value-hex)',
                }}
              >
                {order.eventDetails?.eventName || order.contactDetails?.contactName || 'Order'}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography>{formatDate(order.completedAt)}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography>{formatMoney(order.amount)}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography>
                {formatMoney(order.feeAmount)} ({order.feePercentage}%)
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography>{formatMoney(order.vendorPayoutAmount)}</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <Dialog open={paidDialogOpen} onClose={() => setPaidDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark Payout as Paid</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Bank / transaction reference"
            fullWidth
            value={bankReference}
            onChange={(e) => setBankReference(e.target.value)}
            placeholder="e.g. bank transfer ID or transaction reference"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaidDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmPaid} variant="contained">
            Mark as Paid
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={failDialogOpen} onClose={() => setFailDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark Payout as Failed</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Failure Reason"
            fullWidth
            multiline
            rows={3}
            value={failureReason}
            onChange={(e) => setFailureReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFailDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmFailed} variant="contained" color="error">
            Mark as Failed
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayoutRequestDetail;
